import { formatDurationToDayHHMM } from "@utils/utils";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useRbtResponsesToBcbaQuestionsRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface RBTResponseToBCBAQuestionRawDataProps {
  forResponseRate: boolean;
}

interface RBTResponseToBCBAQuestionRawDataType {
  bcbaName: string;
  questionAskedAt: string;
  respondedOn: string;
  responseTimeInSeconds: number;
  rbtName: string;
}

export const RBTResponseToBCBAQuestionRawData: React.FC<RBTResponseToBCBAQuestionRawDataProps> = ({
  forResponseRate,
}) => {
  const { selectedRBTDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();

  const [selectedRBT, setSelectedRBT] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedRBTDates);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedRBT, selectedClient]);

  const { data, isFetching } = useRbtResponsesToBcbaQuestionsRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      rbtProviderIds: selectedRBT.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
      forResponseRate,
    },
    {
      queryKey: ["rbtResponsesToBcbaQuestionsRawData", dates, selectedRBT, pageNo, pageSize, selectedClient],
    },
  );
  const columns: Column<RBTResponseToBCBAQuestionRawDataType>[] = [
    {
      title: "BCBA",
      key: "bcbaName",
      dataIndex: "bcbaName",
      render: (value) => value || "-",
    },
    {
      title: "Question Asked (Date/time)",
      key: "questionAskedAt",
      dataIndex: "questionAskedAt",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Responded On",
      key: "respondedOn",
      dataIndex: "respondedOn",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Elapsed Response Time (min)",
      key: "responseTimeInSeconds",
      dataIndex: "responseTimeInSeconds",
      render: (value: string | number) => (value ? formatDurationToDayHHMM(value as number) : "-"),
    },
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (value) => value || "-",
    },
  ];
  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedRBTs={selectedRBT}
        setSelectedRBTs={setSelectedRBT}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showBCBAFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.rbtResponsesToBcbaQuestionsRawData?.rawData
            ? (data.rbtResponsesToBcbaQuestionsRawData?.rawData as RBTResponseToBCBAQuestionRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.rbtResponsesToBcbaQuestionsRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
